import axios from "axios";
import { tokenStore } from "./tokenStore";
import { refreshAccessToken } from "./authRefresh";

const getBaseURL = (serverOption: "base" | "orchestrator") => {
  const envKey = `REACT_APP_${serverOption.toUpperCase()}_URL`;
  const envURL = process.env[envKey];
  if (envURL && envURL.trim().length) {
    return envURL;
  }
  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location;
    const fallback = `${protocol}//${hostname}:1245`;
    return fallback;
  }
  return undefined;
};

const request = (serverOption?: "base" | "orchestrator") => {
  const axiosInstance = axios.create({
    baseURL: getBaseURL(serverOption ?? "base"),
    timeout: 100000,
    // Sends/receives the httpOnly refresh-token cookie on /auth/* requests.
    withCredentials: true,
  });

  // Attach the current access token to every request. The API now requires a
  // valid JWT on all data endpoints, so unauthenticated calls will 401. The
  // token comes from the in-memory store — never localStorage (issue #6).
  axiosInstance.interceptors.request.use((config) => {
    const token = tokenStore.get();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use((response: any) => {
    return response;
  }, error);

  return axiosInstance;
};

async function error(error: any) {
  if (typeof error.response === "undefined") {
    return await Promise.reject(error);
  }

  const originalRequest = error.config;

  if (
    error.response.status === 401 &&
    originalRequest &&
    !originalRequest._retry &&
    !originalRequest.url?.includes("/auth/refresh") &&
    !originalRequest.url?.includes("/auth/login")
  ) {
    originalRequest._retry = true;
    const newAccessToken = await refreshAccessToken();
    if (newAccessToken) {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axios(originalRequest);
    }
  }

  return await Promise.reject(error);
}

export { request };
